import { createRef } from 'react';
import { connect } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import MyAccountMenuUtils from './my-account-menu-utils';

const BonusTypes = MojitoServices.Bonus.types;
const { UIViewImplementation } = MojitoCore.Presentation;
const { PasswordChange, BetHistoryDetails } = MojitoModules;
const Bonuses = MojitoModules.Bonuses.module;
const BetHistory = MojitoModules.BetHistory.Sports.module;
const AppSettings = MojitoModules.AppSettings.module;
const { DocumentHead, LinkMenu, PageHeader, DefaultHeadMetaFactory } =
    MojitoPresentation.Components;
const intentActions = MojitoCore.Intents.actions;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { LOGGED_IN } = MojitoServices.Authentication.types.STATES;
const MyAccountMenuItemDescriptors = MojitoModules.MyAccount.menuDescriptors;
const { actions: betHistoryActions } = MojitoServices.Bets.betHistorySlice;
const {
    BONUSES,
    ACTIVE_BONUSES,
    IN_PROGRESS_BONUSES,
    BONUS_HISTORY,
    PASSWORD,
    BET_HISTORY,
    BET_HISTORY_DETAILS,
    APP_SETTINGS,
} = MyAccountMenuItemDescriptors.ITEMS;
const { dispatch } = MojitoCore.Services.redux.store;

const BONUS_FILTER_URL_MAPPER = {
    [ACTIVE_BONUSES.ID]: BonusTypes.BONUS_FILTER.ACTIVE,
    [IN_PROGRESS_BONUSES.ID]: BonusTypes.BONUS_FILTER.IN_PROGRESS,
    [BONUS_HISTORY.ID]: BonusTypes.BONUS_FILTER.HISTORIC,
};

class MyAccountPageView extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.headerWrapperRef = createRef();

        // Creating the MetaFactory instance here to avoid re-creating it on every render()
        const metaFactory = this.config.documentHeadMetaFactory || DefaultHeadMetaFactory;
        this.documentHeadMetaFactory = new metaFactory(this.l10n);

        this.onBackClicked = this.onBackClicked.bind(this);
        this.onBetHistoryBetClick = this.onBetHistoryBetClick.bind(this);
        this.triggerItemIntent = this.triggerItemIntent.bind(this);
    }

    componentWillUnmount() {
        if (this.config.shouldClearFilters) {
            dispatch(betHistoryActions.reset());
        }
    }

    componentDidUpdate(prevProps) {
        // Changing selection to an item with an intent
        // should trigger the intent.
        if (prevProps.itemId !== this.props.itemId) {
            // Fullscreen mode should align header to top when navigating to a new account view
            if (this.config.renderFullscreenMenu) {
                // With mobile/fullscreen menu it doesn't seem to work scrolling to align top,
                // so try to align to bottom which gives the same result.
                this.headerWrapperRef.current &&
                    this.headerWrapperRef.current.scrollIntoView(false);
            }

            this.triggerItemIntent(this.props.itemId);
        }
    }

    triggerItemIntent(itemId) {
        const { dispatch } = this.props;

        const item = MyAccountMenuUtils.findItem(this.config.menuSections, itemId);

        if (item && item.url) {
            dispatch(
                intentActions.publishIntent(MojitoPresentation.Base.Intent.Types.OPEN_URL, {
                    url: item.url,
                })
            );
        }
    }

    resolveItemIntent(item) {
        if (item.intent) {
            return item.intent;
        }

        return MyAccountMenuItemDescriptors.getIntentFromItemId(item.id);
    }

    isVisibleItem(item) {
        const isVisible =
            (item.id !== APP_SETTINGS.ID || !this.config.hideAppSettingsLink) && !item.hideFromMenu;
        return isVisible;
    }

    buildMenuSection(section, selectedItemId) {
        return {
            header: {
                label: this.resolveString(section.label),
            },
            items: (section.items || []).reduce((items, item) => {
                if (!this.isVisibleItem(item)) {
                    return items;
                }

                const isSelected = item.id === selectedItemId;

                items.push({
                    isSelected,
                    label: this.resolveString(item.label),
                    icon: isSelected ? this.config.menuSelectionIcon : undefined,
                    intent: this.resolveItemIntent(item),
                });

                return items;
            }, []),
        };
    }

    getMenuItems(selectedItemId) {
        return this.config.menuSections.map(section =>
            this.buildMenuSection(section, selectedItemId)
        );
    }

    getContent(selectedItemId) {
        switch (selectedItemId) {
            case BONUSES.ID: {
                const onSelectedTabChange = tabId => {
                    const partId = Object.keys(BONUS_FILTER_URL_MAPPER).find(bonusUrl => {
                        return BONUS_FILTER_URL_MAPPER[bonusUrl] === tabId;
                    });
                    IntentActionTrigger.showAccountPage(BONUSES.ID, partId);
                };

                return (
                    <Bonuses
                        defaultTab={BONUS_FILTER_URL_MAPPER[this.props.partId]}
                        onSelectedTabChange={onSelectedTabChange}
                    />
                );
            }

            case PASSWORD.ID:
                return <PasswordChange />;

            case BET_HISTORY.ID:
                return <BetHistory onBetClick={this.onBetHistoryBetClick} />;

            case BET_HISTORY_DETAILS.ID:
                return <BetHistoryDetails betId={this.props.partId} />;

            case APP_SETTINGS.ID:
                return <AppSettings />;

            default:
                return null;
        }
    }

    onBetHistoryBetClick(betId) {
        IntentActionTrigger.showAccountPage(BET_HISTORY_DETAILS.ID, betId);
    }

    renderContent(selectedItemId) {
        const item = MyAccountMenuUtils.findItem(this.config.menuSections, selectedItemId);

        // A custom intent has higher prio than showing build in content.
        if (item && item.intent) {
            return null;
        }

        const content = this.getContent(selectedItemId);

        if (!content) {
            return null;
        }

        // Show header above content when fullscreen menu is NOT configured
        const showHeaderAboveContent = !this.config.renderFullscreenMenu;

        return (
            <div style={this.style.content} key="content">
                {showHeaderAboveContent && this.renderHeader(selectedItemId)}
                {content}
            </div>
        );
    }

    onBackClicked(intent) {
        const { dispatch } = this.props;
        const handled = this.config.backButtonCallback && this.config.backButtonCallback(intent);
        if (handled) {
            return;
        }

        if (intent && intent.data) {
            dispatch(intentActions.publishIntent(intent.type, intent.data));
        } else {
            IntentActionTrigger.showAccountPage();
        }
    }

    renderHeader(selectedItemId) {
        const menuItemConfig =
            MyAccountMenuUtils.findItem(
                this.config.menuSections,
                selectedItemId,
                this.props.partId
            ) || {};
        let label;
        let showBackButton;

        // If we have a selection and is configured to render the path then resolve it...
        if (
            selectedItemId &&
            this.config.menuSections.length > 0 &&
            !this.config.menuItemPathInHeader.hide
        ) {
            label = this.resolveString(menuItemConfig.label);
            // Config was intentionally named to hide instead of show, because it is not possible to set a default
            // value for it. So if it is not configured, it will be a falsy value and thus not hidden.
            showBackButton = !menuItemConfig.hideBackButton;
        } else {
            label = this.resolveString('$MY_ACCOUNT_PAGE.TITLE_LABEL');
            showBackButton = false;
        }

        const itemIntent = this.resolveItemIntent({ id: this.getBackItemId(selectedItemId) });
        const handleOnBackClick = intent => () => this.onBackClicked(intent);

        // Note: Key is set in order to re-create the element when the config changes
        return (
            <div ref={this.headerWrapperRef}>
                <PageHeader
                    label={label}
                    showBackButton={showBackButton}
                    config={this.config.pageHeader}
                    onBackClicked={handleOnBackClick(itemIntent)}
                />
            </div>
        );
    }

    getBackItemId(selectedItemId) {
        return selectedItemId === BET_HISTORY_DETAILS.ID ? BET_HISTORY.ID : undefined;
    }

    renderMenu(selectedItemId) {
        // If fullscreen menu is configured render it only when no item is selected
        if (this.config.renderFullscreenMenu && !!selectedItemId) {
            return null;
        }

        return (
            <div style={this.style.menu} key="menu">
                <LinkMenu config={this.config.linkMenu} items={this.getMenuItems(selectedItemId)} />
            </div>
        );
    }

    renderDocumentHead() {
        const headData = {
            title: '$MY_ACCOUNT_PAGE.PAGE_TITLE',
            keywords: '$MY_ACCOUNT_PAGE.PAGE_KEYWORDS',
            description: '$MY_ACCOUNT_PAGE.PAGE_DESCRIPTION',
        };

        return <DocumentHead data={headData} metaFactory={this.documentHeadMetaFactory} />;
    }

    render() {
        return (
            <div className="ta-MyAccountPageView">
                {this.renderDocumentHead()}
                {this.config.renderFullscreenMenu && this.renderHeader(this.props.itemId)}
                <div style={this.style.container}>
                    {this.appContext().getLoginState() === LOGGED_IN && [
                        this.renderMenu(this.props.itemId),
                        this.renderContent(this.props.itemId),
                    ]}
                </div>
            </div>
        );
    }
}

MyAccountPageView.getStyle = function (config, applicationmode, merge) {
    const style = {
        container: merge(
            {
                display: 'flex',
            },
            config.container
        ),
        menu: merge(config.menu.border, {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: config.renderFullscreenMenu ? '100%' : config.menu.width,
        }),
        content: {
            display: 'flex',
            flex: '1 1 100%',
            flexDirection: 'column',
            padding: config.content.padding,
            margin: config.content.margin,
        },
    };

    return style;
};

export default connect()(MyAccountPageView);
